import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useState } from 'react'

interface DeclineCommentModalProps {
  title: string
  onConfirm: (comment: string) => void
  onCancel: () => void
  isSubmitting?: boolean
}

const DeclineCommentModal: React.FC<DeclineCommentModalProps> = ({
  title,
  onConfirm,
  onCancel,
  isSubmitting = false,
}) => {
  const [comment, setComment] = useState('')
  const [error, setError] = useState<string | null>(null)

  const handleConfirm = () => {
    const trimmedComment = comment.trim()
    if (!trimmedComment) {
      setError('Please provide a reason for declining.')
      return
    }
    setError(null)
    onConfirm(trimmedComment)
  }

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value.slice(0, 1000)
    setComment(value)
    if (error && value.trim()) {
      setError(null)
    }
  }

  return (
    <div className={css(styles.overlay)}>
      <div className={css(styles.modal)}>
        <div className={css(styles.header)}>
          <h3 className={css(styles.title)}>{title}</h3>
        </div>
        <div className={css(styles.content)}>
          <p className={css(styles.description)}>
            Please provide a reason for declining. This comment is required.
          </p>
          <textarea
            value={comment}
            onChange={handleCommentChange}
            placeholder="Enter your reason for declining..."
            className={css(styles.textarea, error && styles.textareaError)}
            rows={4}
            disabled={isSubmitting}
            maxLength={1000}
          />
          <div className={css(styles.characterCounter)}>
            {comment.length}/1000 characters
          </div>
          {error && <p className={css(styles.errorText)}>{error}</p>}
        </div>
        <div className={css(styles.footer)}>
          <button
            onClick={onCancel}
            className={css(styles.cancelBtn)}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className={css(styles.confirmBtn)}
            disabled={isSubmitting || !comment.trim()}
          >
            {isSubmitting ? 'Declining...' : 'Confirm Decline'}
          </button>
        </div>
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1200,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '500px',
    margin: '20px',
  },
  header: {
    padding: '24px 24px 0 24px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  content: {
    padding: '16px 24px',
  },
  description: {
    margin: '0 0 16px 0',
    fontSize: '14px',
    color: '#6B7280',
  },
  textarea: {
    width: '100%',
    minHeight: '100px',
    padding: '12px',
    border: '1px solid #D1D5DB',
    borderRadius: '4px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
    ':focus': {
      outline: 'none',
      borderColor: '#2563EB',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    ':disabled': {
      backgroundColor: '#F9FAFB',
      cursor: 'not-allowed',
    },
  },
  textareaError: {
    borderColor: '#EF4444',
    ':focus': {
      borderColor: '#EF4444',
      boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.1)',
    },
  },
  characterCounter: {
    fontSize: '12px',
    color: '#6B7280',
    textAlign: 'right',
    marginTop: '4px',
  },
  errorText: {
    margin: '8px 0 0 0',
    fontSize: '12px',
    color: '#EF4444',
  },
  footer: {
    padding: '0 24px 24px 24px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
  },
  cancelBtn: {
    backgroundColor: '#F3F4F6',
    color: '#374151',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#E5E7EB',
    },
    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
  confirmBtn: {
    backgroundColor: '#EF4444',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#DC2626',
    },
    ':disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },
})

export default DeclineCommentModal
